import { useState } from 'react'
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Avatar,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Box,
    Tooltip,
} from '@mui/material'
import {
    Menu as MenuIcon,
    Person,
    Logout,
    Settings,
    DarkMode,
    LightMode,
} from '@mui/icons-material'
import { useAuthStore } from '@store/authStore'
import { useUIStore } from '@store/uiStore'
import { useAuth } from '@hooks/useAuth'
import { useTranslation } from '@i18n'
import LanguageSwitcher from '@components/Common/LanguageSwitcher'
import NotificationBell from '@components/Dashboard/NotificationBell'

const Header = ({ onMenuClick }) => {
    const { t, language } = useTranslation()
    const { user } = useAuthStore()
    const { darkMode, toggleDarkMode, toggleSidebarCollapsed, themeSettings } = useUIStore()
    const { logout } = useAuth()
    const [anchorEl, setAnchorEl] = useState(null)
    const open = Boolean(anchorEl)

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleLogout = () => {
        handleClose()
        logout()
    }

    const getUserName = () => {
        if (language === 'ar' && user?.name_ar) {
            return user.name_ar
        }
        return user?.name_en || user?.email || 'User'
    }

    const getInitials = () => {
        const name = getUserName()
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2)
    }

    const getUserRoleLabel = () => {
        const roleLabels = {
            admin: language === 'ar' ? 'مدير النظام' : 'Admin',
            form_manager: language === 'ar' ? 'مدير نماذج' : 'Form Manager',
            dashboard: language === 'ar' ? 'مستخدم' : 'User',
            department: language === 'ar' ? 'قسم' : 'Department',
        }
        return roleLabels[user?.user_type] || user?.user_type
    }

    return (
        <AppBar
            position="fixed"
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                bgcolor: 'background.paper',
                color: 'text.primary',
                boxShadow: 1,
            }}
        >
            <Toolbar>
                {/* Menu Toggle Button - FIRST */}
                <IconButton
                    color="inherit"
                    onClick={() => {
                        // Toggle sidebar on desktop, open mobile drawer on mobile
                        toggleSidebarCollapsed()
                        onMenuClick?.()
                    }}
                    sx={{
                        mr: 1.5,
                        p: 1,
                        borderRadius: 1,
                        '&:hover': {
                            bgcolor: 'action.hover',
                        },
                    }}
                >
                    <MenuIcon />
                </IconButton>

                {/* Logo - After toggle button */}
                <Box
                    component="img"
                    src={themeSettings?.logoUrl || '/moj-logo.png'}
                    alt="System Logo"
                    sx={{
                        height: 36,
                        width: 'auto',
                        ml: 2,
                        mr: 2,
                    }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
                    <Typography
                        variant="h6"
                        noWrap
                        component="div"
                        sx={{
                            background: 'linear-gradient(135deg, #1a3a6b 0%, #c9a227 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontWeight: 700,
                        }}
                    >
                        {language === 'ar' ? (themeSettings?.systemNameAr || 'وزارة العدل') : (themeSettings?.systemNameEn || 'MINISTRY OF JUSTICE')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Theme Toggle */}
                    <Tooltip title={darkMode ? t('settings.lightMode') : t('settings.darkMode')}>
                        <IconButton onClick={toggleDarkMode} color="inherit">
                            {darkMode ? <LightMode /> : <DarkMode />}
                        </IconButton>
                    </Tooltip>

                    {/* Language Switcher */}
                    <LanguageSwitcher />

                    {/* Notifications */}
                    <NotificationBell />

                    {/* User Menu */}
                    <Tooltip title={getUserName()}>
                        <IconButton
                            onClick={handleClick}
                            size="small"
                            sx={{ ml: 1 }}
                            aria-controls={open ? 'account-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                        >
                            <Avatar
                                sx={{
                                    width: 36,
                                    height: 36,
                                    bgcolor: 'primary.main',
                                    fontSize: '0.875rem',
                                }}
                            >
                                {getInitials()}
                            </Avatar>
                        </IconButton>
                    </Tooltip>
                </Box>

                <Menu
                    id="account-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                        'aria-labelledby': 'account-button',
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    PaperProps={{
                        sx: { minWidth: 200, mt: 1 },
                    }}
                >
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            {getUserName()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {user?.email}
                        </Typography>
                        <Typography variant="caption" color="primary.main">
                            {getUserRoleLabel()}
                        </Typography>
                    </Box>

                    <Divider />

                    <MenuItem onClick={handleClose}>
                        <ListItemIcon>
                            <Person fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Profile</ListItemText>
                    </MenuItem>

                    <MenuItem onClick={handleClose}>
                        <ListItemIcon>
                            <Settings fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{t('nav.settings')}</ListItemText>
                    </MenuItem>

                    <Divider />

                    <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                            <Logout fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText sx={{ color: 'error.main' }}>{t('auth.logout')}</ListItemText>
                    </MenuItem>
                </Menu>
            </Toolbar>
        </AppBar>
    )
}

export default Header
